import { actionCreator, ActionReturnType, Loading, loadingNotStarted, loadingIsLoading, loadingFinished, loadingFailed } from "@itk/typescript-utils";
import {
  Model_AttributeDetailsGet,
  Model_ConfigurationGet,
  Model_ConfiguratorOrderOut,
  Model_SystemProductConfigurationGet,
  Model_SystemProductConfigurationItemAttributePut,
  Model_SystemProductConfigurationItemPut,
  Model_SystemProductConfigurationPut
} from "src/backend/openapi/v1";
import { MakeSomeRequired } from "src/utils/typescript-utils";

export enum Action {
  GetConfigurationRequest = "@@ITK:SRC:PAGES:CONFIGURE-PRODUCT:CONFIGURE-PRODUCT-STORE.TS:ACTION:GETCONFIGURATIONREQUEST",
  GetConfigurationSuccess = "@@ITK:SRC:PAGES:CONFIGURE-PRODUCT:CONFIGURE-PRODUCT-STORE.TS:ACTION:GETCONFIGURATIONSUCCESS",
  GetConfigurationFail = "@@ITK:SRC:PAGES:CONFIGURE-PRODUCT:CONFIGURE-PRODUCT-STORE.TS:ACTION:GETCONFIGURATIONFAIL",

  UpdateProjectLineItemAttributeRequest = "@@ITK:SRC:PAGES:CONFIGURE-PRODUCT:CONFIGURE-PRODUCT-STORE.TS:ACTION:UPDATEPROJECTLINEITEMATTRIBUTEREQUEST",
  UpdateProjectLineItemRequest = "@@ITK:SRC:PAGES:CONFIGURE-PRODUCT:CONFIGURE-PRODUCT-STORE.TS:ACTION:UPDATEPROJECTLINEITEMREQUEST",

  SetSelectedProjectLineItemId = "@@ITK:SRC:PAGES:CONFIGURE-PRODUCT:CONFIGURE-PRODUCT-STORE.TS:ACTION:SETSELECTEDPROJECTLINEITEMID",

  UpdateProjectLineItemAttributes = "@@ITK:SRC:PAGES:CONFIGURE-PRODUCT:CONFIGURE-PRODUCT-STORE.TS:ACTION:UPDATEPROJECTLINEITEMATTRIBUTES",

  UpdateConfigurationDescriptionRequest = "@@ITK:SRC:PAGES:CONFIGURE-PRODUCT:CONFIGURE-PRODUCT-STORE.TS:ACTION:UPDATECONFIGURATIONDESCRIPTIONREQUEST",
  UpdateConfigurationDescriptionSuccess = "@@ITK:SRC:PAGES:CONFIGURE-PRODUCT:CONFIGURE-PRODUCT-STORE.TS:ACTION:UPDATECONFIGURATIONDESCRIPTIONSUCCESS",

  GetGenerateConfigurationOrderRequest = "@@ITK:SRC:PAGES:CONFIGURE-PRODUCT:CONFIGURE-PRODUCT-STORE.TS:ACTION:GETGENERATECONFIGURATIONORDERREQUEST",
  GetGenerateConfigurationOrderSuccess = "@@ITK:SRC:PAGES:CONFIGURE-PRODUCT:CONFIGURE-PRODUCT-STORE.TS:ACTION:GETGENERATECONFIGURATIONORDERSUCCESS",
  GetGenerateConfigurationOrderFail = "@@ITK:SRC:PAGES:CONFIGURE-PRODUCT:CONFIGURE-PRODUCT-STORE.TS:ACTION:GETGENERATECONFIGURATIONORDERFAIL",

  Clear = "@@ITK:SRC:PAGES:CONFIGURE-PRODUCT:CONFIGURE-PRODUCT-STORE.TS:ACTION:CLEAR"
}

type TConfigureProductState = {
  configurationState: Loading<Model_ConfigurationGet>;
  selectedProjectLineItemId?: string;
  generateConfigurationOrder: Loading<Model_ConfiguratorOrderOut>;
};

const initialState: TConfigureProductState = {
  configurationState: loadingNotStarted,
  selectedProjectLineItemId: undefined,
  generateConfigurationOrder: loadingNotStarted
};

export const configureProductActions = {
  getConfigurationRequest: (configurationId: string) => actionCreator(Action.GetConfigurationRequest, { configurationId }),
  getConfigurationSuccess: (data: Model_ConfigurationGet) => actionCreator(Action.GetConfigurationSuccess, { data }),
  getConfigurationFail: () => actionCreator(Action.GetConfigurationFail),

  updateProjectLineItemRequest: (data: MakeSomeRequired<Model_SystemProductConfigurationItemPut, "systemProductConfigurationItemId" | "projectId">) =>
    actionCreator(Action.UpdateProjectLineItemRequest, { data }),

  // shouldDebounce: true for fields that fire this on every keystroke (Text/Integer/Decimal/DimensionType),
  // so the saga can coalesce rapid saves; false for a single discrete commit (selection/click), which
  // should reach the server immediately — see watchUpdateProjectLineItemAttribute (configure-product-saga.ts).
  // onError: invoked by the saga's catch block on save failure, so the caller can drop the input the
  // server refused and fall back to what it holds (see use-attribute-grid-form.ts's failedEditTick)
  updateProjectLineItemAttributeRequest: (data: Required<Model_SystemProductConfigurationItemAttributePut>, shouldDebounce: boolean, onError?: () => void) =>
    actionCreator(Action.UpdateProjectLineItemAttributeRequest, { data, shouldDebounce, onError }),

  // systemProductConfigurationItemId identifies which item to patch in the reducer. attributes may be only a
  // SUBSET of that item's full attribute list (a rule-affected subset from the PUT save, or the full list from
  // the background GET refresh) — the reducer merges by attributeId rather than replacing, and derives isValid
  // from the merged result itself, since the backend no longer returns it here.
  updateProjectLineItemAttributes: (systemProductConfigurationItemId: string, attributes: Model_AttributeDetailsGet[]) =>
    actionCreator(Action.UpdateProjectLineItemAttributes, { systemProductConfigurationItemId, attributes }),

  setSelectedProjectLineItemId: (projectLineItemId: TConfigureProductState["selectedProjectLineItemId"]) => actionCreator(Action.SetSelectedProjectLineItemId, { projectLineItemId }),

  // only systemProductConfigurationId + description are sent; projectId/systemPackageid are left undefined so the backend leaves them untouched
  updateConfigurationDescriptionRequest: (data: MakeSomeRequired<Model_SystemProductConfigurationPut, "systemProductConfigurationId" | "description">) =>
    actionCreator(Action.UpdateConfigurationDescriptionRequest, { data }),
  updateConfigurationDescriptionSuccess: (data: Model_SystemProductConfigurationGet) => actionCreator(Action.UpdateConfigurationDescriptionSuccess, { data }),

  getGenerateConfigurationOrderRequest: (projectId: string, configurationId: string) => actionCreator(Action.GetGenerateConfigurationOrderRequest, { projectId, configurationId }),
  getGenerateConfigurationOrderSuccess: (data: Model_ConfiguratorOrderOut) => actionCreator(Action.GetGenerateConfigurationOrderSuccess, { data }),
  getGenerateConfigurationOrderFail: () => actionCreator(Action.GetGenerateConfigurationOrderFail),

  clear: () => actionCreator(Action.Clear)
};

export const configureProductReducer = (state = initialState, action: ActionReturnType<typeof configureProductActions>): TConfigureProductState => {
  switch (action.type) {
    case Action.GetConfigurationRequest:
      return { ...state, configurationState: loadingIsLoading };
    case Action.GetConfigurationSuccess:
      return { ...state, configurationState: loadingFinished(action.payload.data) };
    case Action.GetConfigurationFail:
      return { ...state, configurationState: loadingFailed };

    case Action.SetSelectedProjectLineItemId:
      return { ...state, selectedProjectLineItemId: action.payload.projectLineItemId };

    case Action.UpdateProjectLineItemAttributes: {
      if (state.configurationState.loading !== "has-loaded") return { ...state };
      return {
        ...state,
        configurationState: loadingFinished({
          ...state.configurationState.data,
          // map over the renamed field and match by the renamed ID field
          systemProductConfigurationItems: state.configurationState.data.systemProductConfigurationItems.map(item => {
            if (item.systemProductConfigurationItemId !== action.payload.systemProductConfigurationItemId) return item;

            // payload.attributes may be only a rule-affected SUBSET of this item's attributes (PUT save) or the
            // item's full list (background GET refresh) — upsert by attributeId instead of replacing wholesale,
            // so attributes absent from the payload (not touched by this edit) are preserved either way
            const existingAttributes = item.attributes ?? [];
            const mergedAttributes = [...existingAttributes];
            for (const updatedAttribute of action.payload.attributes) {
              const existingIndex = mergedAttributes.findIndex(attribute => attribute.attributeId === updatedAttribute.attributeId);
              if (existingIndex === -1) {
                mergedAttributes.push(updatedAttribute);
                continue;
              }

              // DimensionType attributes pack independently-saved Height/Width elements into one `value`
              // array — upsert by part instead of replacing the array wholesale, so a payload carrying only
              // one part can never wipe out the sibling part's already-applied value. Because a part left
              // out is a part left alone, a save that CLEARED one spells that out as an empty element
              // rather than omitting it (see updateProjectLineItemAttribute in configure-product-saga.ts).
              // Every other attribute type only ever has a single part: null element, so this is
              // equivalent to the previous wholesale replace for them.
              const mergedValues = updatedAttribute.value?.length ? [...(mergedAttributes[existingIndex].value ?? [])] : [];

              for (const updatedValue of updatedAttribute.value ?? []) {
                const valueIndex = mergedValues.findIndex(value => value.part === updatedValue.part);
                if (valueIndex === -1) mergedValues.push(updatedValue);
                else mergedValues[valueIndex] = updatedValue;
              }
              mergedAttributes[existingIndex] = { ...updatedAttribute, value: mergedValues };
            }

            return { ...item, attributes: mergedAttributes, isValid: mergedAttributes.every(attribute => attribute.isValid) };
          })
        })
      };
    }

    case Action.UpdateConfigurationDescriptionSuccess: {
      if (state.configurationState.loading !== "has-loaded") return { ...state };
      return {
        ...state,
        // SystemProductConfigurationName is sourced from Description on the backend
        configurationState: loadingFinished({ ...state.configurationState.data, systemProductConfigurationName: action.payload.data.description || "" })
      };
    }

    case Action.GetGenerateConfigurationOrderRequest:
      // Every attribute edit refetches the parts list. Once it has loaded, hold the rows that are on
      // screen instead of dropping back to is-loading — the saga raises the global loading bar for the
      // refresh, so the table no longer collapses into a skeleton and rebuilds on each edit.
      // The skeleton is still shown for the first load, where there is nothing to hold.
      if (state.generateConfigurationOrder.loading === "has-loaded") return { ...state };
      return { ...state, generateConfigurationOrder: loadingIsLoading };
    case Action.GetGenerateConfigurationOrderSuccess:
      return { ...state, generateConfigurationOrder: loadingFinished(action.payload.data) };
    case Action.GetGenerateConfigurationOrderFail:
      return { ...state, generateConfigurationOrder: loadingFailed };

    case Action.Clear:
      return { ...initialState };

    default:
      return state;
  }
};
